import './GPCSubscriptions.scss';

import { array, bool, object } from 'prop-types';
import React, { Fragment, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import { getRequestStatus } from 'state/requests/RequestStatusSelectors';
import PreferencesHeader from 'components/layouts/Preferences/PreferencesHeader';
import PreferencesSection from 'components/layouts/Preferences/shared/PreferencesSection';
import GPCAccountAndFundAlerts from 'components/layouts/Preferences/Subscriptions/GPCAccountAndFundAlerts';
import GPCAdditionalNotifications from 'components/layouts/Preferences/Subscriptions/GPCAdditionalNotifications';
import FixedSubmitFooter from 'components/layouts/Preferences/shared/FixedSubmitFooter/FixedSubmitFooter';
import If from 'components/shared/If/If';
import { InvalidOrMissingEmail } from 'components/layouts/Preferences/InvalidOrMissingEmail';
import {
  getMutualFundInformation,
  getCustomerSubscriptions,
  saveSubscriptions
} from 'services/Rest';
import {
  getAccountFundAlertsSelectedType,
  MARKET_WRAP_UP_IDS
} from 'services/Preferences';

import {
  getListOfMutualFundGroups,
  getMutualFunds,
  getUserMutualFundsWithIVData
} from 'state/preferences/PreferencesSelectors';
import {
  getMappedSubscriptions,
  getShouldDisableSave,
  getInitialSubscriptions
} from 'state/subscriptions/SubscriptionsSelectors';
import { getView, isSalesforceRepEnabled } from 'state/view/ViewSelectors';
import { getPrimaryEmailAddress } from 'state/customer/CustomerSelectors';
import { getHasUpdateSubscriptions } from 'state/requests/RequestStatusSelectors';
import { getHasInvalidOrNoEmailAddress } from 'state/contactMethodMaintenance/ContactMethodMaintenanceSelectors';
import { updateSubscriptions } from 'state/subscriptions/SubscriptionsActions';
import Store from 'state/Store';
import {
  getFundsByRadioSelection,
  getBroadAssetsByRadioSelection,
  dailyPricesEmptySelection,
  generateDailyPricesPayload,
  generateMonthlyFundPerformancePayload,
  monthlyFundPerformanceEmptySelection,
  generateSubscriptionsPayload
} from 'services/Subscriptions';
import { REQUEST_STATES } from 'state/requests/RequestStatusReducer';
import { getSubscriptionKey } from 'components/shared/GroupedAccountList/util';


export const getPayloadUpdateAccountFundAlerts = (
  key,
  value,
  subscriptions,
  mutualFunds,
  userMutualFundsWithIVData,
  isTracOnly
) => {
  const {
    digitalSubscriptions: {
      newsLetterIds,
      priceSubscriptions,
      performanceSubscriptions,
      taxForms
    }
  } = subscriptions;

  let payload = {};

  if (key === 'marketWrapups') {
    if (value) {
      payload = {
        digitalSubscriptions: {
          newsLetterIds: newsLetterIds.concat(MARKET_WRAP_UP_IDS)
        }
      };
    } else {
      // filter out market wrap up ids from state subscriptions
      const remainingNewsletters = newsLetterIds.filter(
        (id) => MARKET_WRAP_UP_IDS.indexOf(id) === -1
      );
      payload = {
        digitalSubscriptions: {
          newsLetterIds: remainingNewsletters
        }
      };
    }

    payload.digitalSubscriptions.taxForms = taxForms;
    payload.digitalSubscriptions.priceSubscriptions = priceSubscriptions;
    payload.digitalSubscriptions.performanceSubscriptions =
      performanceSubscriptions;
  }
  if (key === 'taxForms') {
    if (value) {
      payload = {
        digitalSubscriptions: {
          taxForms: true
        }
      };
    } else {
      payload = {
        digitalSubscriptions: {
          taxForms: false
        }
      };
    }

    payload.digitalSubscriptions.newsLetterIds = newsLetterIds;
    payload.digitalSubscriptions.priceSubscriptions = priceSubscriptions;
    payload.digitalSubscriptions.performanceSubscriptions =
      performanceSubscriptions;
  }

  const selectedTrpCodes = value && value['trpCodes'] ? value.trpCodes : [];


  const performanceTypeSelected = getAccountFundAlertsSelectedType(
    key,
    value,
    performanceSubscriptions,
    isTracOnly
  );
  const performanceCodes = getFundsByRadioSelection(
    performanceTypeSelected,
    selectedTrpCodes,
    mutualFunds,
    userMutualFundsWithIVData
  );
  const performanceAssetClasses = getBroadAssetsByRadioSelection(
    performanceTypeSelected,
    selectedTrpCodes,
    mutualFunds
  );

  const priceTypeSelected = getAccountFundAlertsSelectedType(
    key,
    value,
    priceSubscriptions,
    isTracOnly
  );
  const priceCodes = getFundsByRadioSelection(
    priceTypeSelected,
    selectedTrpCodes,
    mutualFunds,
    userMutualFundsWithIVData
  );

  const priceAssetClasses = getBroadAssetsByRadioSelection(
    priceTypeSelected,
    selectedTrpCodes,
    mutualFunds
  );

  if (
    key === 'myFundsDailyPrices' ||
    key === 'allFundsDailyPrices' ||
    key === 'selectDailyFundPrices' ||
    key === 'dailyFundPricesSelected'
  ) {
    if (value) {
      payload = generateDailyPricesPayload(
        priceTypeSelected,
        priceAssetClasses,
        priceCodes,
        newsLetterIds,
        performanceSubscriptions,
        taxForms
      );
    } else {
      payload = dailyPricesEmptySelection(
        newsLetterIds
      );
    }
  }

  if (
    key === 'myFundsMonthlyFundPerformance' ||
    key === 'allFundsMonthlyFundPerformance' ||
    key === 'selectMonthlyFundPerformance' ||
    key === 'monthlyFundPerformancesSelected'
  ) {
    if (value) {
      payload = generateMonthlyFundPerformancePayload(
        performanceTypeSelected,
        performanceAssetClasses,
        performanceCodes,
        newsLetterIds,
        priceSubscriptions,
        taxForms
      );
    } else {
      payload = monthlyFundPerformanceEmptySelection(
        newsLetterIds
      );
    }
  }

  return payload;
};

export const getPayloadOptStatus = (key, value, subscriptions) => {
  const { optOutElections } = subscriptions;

    const payload = {
      optOutElections: []
    };

    if (key.includes('optOutAll') && value) {
      //The key is the same as the element ID. In order to have a reusable change handler for all checkboxes, we append optOutAll to the ID
      //We check if it is optOutAll, then strip that off to get the categoryName we want to change. categoryName = subkey here
      //Example of key value: optOutAllT. Rowe Price Personal Investing
      //When stripped, we will update all opt out elections for personal investing.
      const subKey = getSubscriptionKey(key.replace('optOutAll', ''));
      optOutElections
        .filter((o) => o.categoryName.includes(subKey))
        .forEach((ooe) => {
          payload.optOutElections.push({
            categoryId: ooe.categoryId,
            categoryName: ooe.categoryName,
            optOut: true
          });
        });
    } else {
      const category = optOutElections.find((c) => c.categoryName === key);
      if (category) {
        const { categoryId, categoryName } = category;

        payload.optOutElections.push({
          categoryId,
          categoryName,
          optOut: !value
        });
      }
    }

    return payload;
};

const GPCSubscriptions = ({
  customerEmail,
  hasInvalidOrMissingEmail,
  history,
  initialSubscriptions,
  isSalesforceRepEnabled,
  mutualFunds,
  requestStatus,
  subscriptions,
  userMutualFundsWithIVData,
  view,
  shouldDisableSave
}) => {
  const [showSave, setShowSave] = useState(false);

  useEffect(() => {
    getMutualFundInformation();
    getCustomerSubscriptions();
  }, []);

  useEffect(() => {
    const { updateSubscriptions } = requestStatus;

    if (updateSubscriptions === REQUEST_STATES.received) {
      setShowSave(false);
    }

    return () => {};
  }, [requestStatus]);

  const updateAccountFundAlerts = (key, value) => {
    setShowSave(true);

    const { isTracOnly } = view;
    if(subscriptions && mutualFunds && userMutualFundsWithIVData) {
      const payload = getPayloadUpdateAccountFundAlerts(
        key,
        value,
        subscriptions,
        mutualFunds,
        userMutualFundsWithIVData,
        isTracOnly
      );

      Store.dispatch(updateSubscriptions(payload));
    }
  };

  const fundSelectionChange = () => {
    setShowSave(true);
    //this.setState({ [key]: value });
  };

  const optStatus = (key, value) => {
    setShowSave(true);

    const payload = getPayloadOptStatus(key, value, subscriptions);

    if (payload.optOutElections.length > 0) {
      payload.optOutElections.forEach((el) =>
        Store.dispatch(updateSubscriptions({ optOutElections: el }))
      );
    }
  };

  const handleSave = () => {
    let updatedSubscriptions = generateSubscriptionsPayload(
      initialSubscriptions,
      subscriptions
    );

    customerEmail.disposition = {};
    customerEmail.expirationDate = '';

    const updateSubscriptionsPayload = {
      ...updatedSubscriptions,
      optOutElections: []
    };

    let payload = {
      emailAddress: customerEmail,
      subscriptions: updateSubscriptionsPayload
    };

    saveSubscriptions(payload, subscriptions);
  };

  const handleCancel = () => {
    setShowSave(false);
    history.push('/profile');
  };

  return (
    <Fragment>
      {!isSalesforceRepEnabled && (
        <PreferencesHeader>
          <div className="GPC-subscriptions-header">Subscriptions</div>
        </PreferencesHeader>
      )}
      <PreferencesSection
        className="GPC-subscriptions-section"
        fullBackground={true}>
        <div className="GPC-subscriptions-page">
          <If test={hasInvalidOrMissingEmail}>
            <InvalidOrMissingEmail page="subscription" view={view} />
          </If>
          <div className="GPC-subscriptions-container">
            <Fragment>
              <div className="GPC-subscriptions-column fill-column">
                <GPCAccountAndFundAlerts
                  subscriptions={subscriptions}
                  providedOnChange={(key, value) =>
                    updateAccountFundAlerts(key, value)
                  }
                  onFundSelectionChange={() => fundSelectionChange()}
                />
              </div>
              <div className="GPC-subscriptions-column">
                <GPCAdditionalNotifications
                  subscriptions={subscriptions}
                  optStatus={optStatus}
                />
              </div>
            </Fragment>
          </div>
          <FixedSubmitFooter
            cancel={() => handleCancel()}
            requestStatusKey="updateSubscriptions"
            shouldShow={showSave}
            submit={() => handleSave()}
            shouldDisableSubmit={shouldDisableSave}
            shouldDisable
          />
        </div>
      </PreferencesSection>
    </Fragment>
  );
};

GPCSubscriptions.propTypes = {
  history: object.isRequired,
  listOfMutualFundGroups: array.isRequired,
  requestStatus: object.isRequired,
  hasInvalidOrMissingEmail: bool.isRequired
};

const mapStateToProps = (state) => ({
  isSalesforceRepEnabled: isSalesforceRepEnabled(state),
  hasUpdateSubscriptions: getHasUpdateSubscriptions(state),
  listOfMutualFundGroups: getListOfMutualFundGroups(state),
  mutualFunds: getMutualFunds(state),
  userMutualFundsWithIVData: getUserMutualFundsWithIVData(state),
  requestStatus: getRequestStatus(state),
  subscriptions: getMappedSubscriptions(state),
  initialSubscriptions: getInitialSubscriptions(state),
  hasInvalidOrMissingEmail: getHasInvalidOrNoEmailAddress(state),
  customerEmail: getPrimaryEmailAddress(state),
  view: getView(state),
  router: state.router,
  shouldDisableSave: getShouldDisableSave(state),
});

export default withRouter(connect(mapStateToProps)(GPCSubscriptions));
